import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { SunIcon, MoonIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';

function Navbar() {
  const { darkMode, setDarkMode } = useTheme();
  const { currentUser, logout } = useAuth();
  const location = useLocation();
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Function to determine if a nav link is active
  const isActive = (path) => {
    return location.pathname === path || 
           (path !== '/' && location.pathname.startsWith(path));
  };

  return (
    <nav className="bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex justify-between h-16">
          <div className="flex">
            <Link to="/" className="flex-shrink-0 flex items-center">
              <span className="text-xl font-bold text-green-500">Content2Learn</span>
            </Link>
            <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
              <Link 
                to="/discovery" 
                className={`inline-flex items-center px-1 pt-1 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 ${isActive('/discovery') ? 'border-b-2 border-green-500 text-gray-900 dark:text-white' : ''}`}
              >
                {t('navbar.discovery')}
              </Link>
              <Link 
                to="/content2learn" 
                className={`inline-flex items-center px-1 pt-1 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 ${isActive('/content2learn') ? 'border-b-2 border-green-500 text-gray-900 dark:text-white' : ''}`}
              >
                Content2Learn
              </Link>
              {currentUser && (
                <Link 
                  to="/history" 
                  className={`inline-flex items-center px-1 pt-1 text-sm font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 ${isActive('/history') ? 'border-b-2 border-green-500 text-gray-900 dark:text-white' : ''}`}
                >
                  {t('navbar.history')}
                </Link>
              )}
            </div>
          </div>
          
          <div className="flex items-center space-x-4">
            {/* Get Started button for mobile - always visible */}
            <Link
              to="/content2learn"
              className="sm:hidden px-3 py-2 text-sm font-medium bg-green-600 text-white hover:bg-green-700 rounded-md"
            >
              Get Started
            </Link>
            
            <button
              onClick={() => setDarkMode(!darkMode)}
              className="p-2 rounded-full text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white focus:outline-none"
              title={t('navbar.toggleDarkMode')}
            >
              {darkMode ? (
                <SunIcon className="h-6 w-6" />
              ) : (
                <MoonIcon className="h-6 w-6" />
              )}
            </button>
            
            {/* Mobile menu button */}
            <button
              className="sm:hidden p-2 rounded-md text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white focus:outline-none"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <svg 
                className="h-6 w-6" 
                fill="none" 
                viewBox="0 0 24 24" 
                stroke="currentColor"
              >
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
            
            <div className="hidden sm:flex">
              {currentUser ? (
                <button
                  onClick={logout}
                  className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors"
                >
                  {t('navbar.logout')}
                </button>
              ) : (
                <div className="flex space-x-2">
                  <Link
                    to="/login"
                    className="px-4 py-2 rounded-md bg-green-600 text-white hover:bg-green-700 transition-colors"
                  >
                    {t('navbar.login')}
                  </Link>
                  <Link
                    to="/signup"
                    className="px-4 py-2 rounded-md border border-green-600 text-green-600 hover:bg-green-50 dark:hover:bg-green-900 transition-colors"
                  >
                    {t('navbar.signup')}
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Mobile menu, show/hide based on menu state */}
      <div className={`sm:hidden ${mobileMenuOpen ? 'block' : 'hidden'}`}>
        <div className="pt-2 pb-3 space-y-1 border-t border-gray-200 dark:border-gray-700">
          <Link
            to="/discovery"
            className={`block pl-3 pr-4 py-2 text-base font-medium ${
              isActive('/discovery')
                ? 'bg-green-50 dark:bg-green-900/30 border-l-4 border-green-500 text-green-700 dark:text-green-400'
                : 'border-l-4 border-transparent text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}
            onClick={() => setMobileMenuOpen(false)}
          >
            {t('navbar.discovery')}
          </Link>
          <Link
            to="/content2learn"
            className={`block pl-3 pr-4 py-2 text-base font-medium ${
              isActive('/content2learn')
                ? 'bg-green-50 dark:bg-green-900/30 border-l-4 border-green-500 text-green-700 dark:text-green-400'
                : 'border-l-4 border-transparent text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}
            onClick={() => setMobileMenuOpen(false)}
          >
            Content2Learn
          </Link>
          {currentUser && (
            <Link
              to="/history"
              className={`block pl-3 pr-4 py-2 text-base font-medium ${
                isActive('/history')
                  ? 'bg-green-50 dark:bg-green-900/30 border-l-4 border-green-500 text-green-700 dark:text-green-400'
                  : 'border-l-4 border-transparent text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
              onClick={() => setMobileMenuOpen(false)}
            >
              {t('navbar.history')}
            </Link>
          )}
          
          {/* Mobile auth buttons */}
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            {currentUser ? (
              <button
                onClick={() => {
                  logout();
                  setMobileMenuOpen(false);
                }}
                className="block w-full text-left pl-3 pr-4 py-2 text-base font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30"
              >
                {t('navbar.logout')}
              </button>
            ) : (
              <div className="space-y-1">
                <Link
                  to="/login"
                  className="block pl-3 pr-4 py-2 text-base font-medium text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/30"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t('navbar.login')}
                </Link>
                <Link
                  to="/signup"
                  className="block pl-3 pr-4 py-2 text-base font-medium text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/30"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {t('navbar.signup')}
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}

export default Navbar;